import React, { ReactElement, useEffect } from 'react'

import { Box, ComponentWithAs, Flex, Icon, ImageProps, Text } from '@chakra-ui/react'
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'

import { Card } from '../Card'

interface CarouselImageProps extends ImageProps {
    key: string
}

interface HorizontalCarouselProps {
    content: {
        title: string
        description: JSX.Element
        image: ReactElement<ComponentWithAs<'img', CarouselImageProps>>
    }[]
    maxCount?: number
    hasDynamicHeight?: boolean
}

const HorizontalCarousel = ({ content, maxCount = 6, hasDynamicHeight = false }: HorizontalCarouselProps) => {
    // Set carousel indicator to bottom left (library default behavior is centered)
    useEffect(() => {
        const indicatorContainer = document.getElementsByClassName('control-dots')[0] as HTMLElement

        if (indicatorContainer) {
            indicatorContainer.style.width = 'fit-content'
            indicatorContainer.style.height = '12px'
        }
    }, [])

    const onChange = (index: number) => {
        // Update displayed image on index change
        const imageContainer = document.getElementById('hc-img-container') as HTMLElement

        if (imageContainer) {
            imageContainer.style.transform = `translateX(${index * -100}%)`
        }
    }

    return (
        <Flex
            direction={['column', 'column', 'row']}
            position={['static', 'static', 'relative']}
            align="center"
            maxW="910px"
            mx="auto"
            py={['0', '0', '24']}
        >
            <Card size={['xs', 'sm']} zIndex={2}>
                <Carousel
                    onChange={onChange}
                    showThumbs={false}
                    showStatus={false}
                    showArrows={false}
                    dynamicHeight={hasDynamicHeight}
                    renderIndicator={(onClickHandler, isSelected) => {
                        return (
                            <Icon
                                onClick={onClickHandler}
                                onKeyDown={onClickHandler}
                                as={isSelected ? MdRadioButtonChecked : MdRadioButtonUnchecked}
                                color={'indigo.500'}
                                cursor="pointer"
                                mr="1.5"
                                w={6}
                                h={6}
                            />
                        )
                    }}
                >
                    {/* Content carousel */}

                    {content.slice(0, maxCount).map((card) => (
                        <Box key={card?.title} pb="12">
                            <Text as="h3" color="indigo.500" fontWeight="bold" fontSize="sm" align="start" mb="3">
                                {card?.title}
                            </Text>

                            <Box>{card?.description}</Box>
                        </Box>
                    ))}
                </Carousel>
            </Card>

            {/* Image carousel */}

            <Flex
                w={['xs', 'sm', 'xl']}
                position={['static', 'static', 'absolute']}
                right="0"
                mt={['4', '4', '0']}
                overflow="hidden"
                boxShadow="xl"
            >
                <Flex id="hc-img-container" transition="all 0.4s">
                    {content.slice(0, maxCount).map((card) => card?.image)}
                </Flex>
            </Flex>
        </Flex>
    )
}

export default HorizontalCarousel
